import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';
import {
  fetchLogsDateChart,
  fetchLogsTypeChart,
  fetchLogs,
  fetchLogsFilter,
} from '../../redux/actions/logActions';
import useDebounce from '../../hooks/useDebounce';
import { parseLogTag, tagPrefix } from '../../utils/parseLogTag';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardRow,
} from './ListMobileLayout';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
);

const EMPTY_OPTIONS = [];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];
const STORAGE_PREFIX = 'error_logs_filter_';
const FILTER_STORAGE_KEYS = [
  'search',
  'page',
  'itemsPerPage',
  'device',
  'osVersion',
  'appVersion',
  'country',
  'errorCode',
];

const getSavedFilter = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const clearSavedFilters = () => {
  FILTER_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    } catch {
      // ignore storage errors
    }
  });
};

function formatReason(reason) {
  if (reason == null) return '—';
  if (typeof reason === 'string') return reason || '—';
  if (typeof reason === 'object' && reason.message != null && reason.message !== '') {
    return reason.message;
  }
  return '—';
}

function TagCell({ item }) {
  const { tagLabel, taskId } = parseLogTag(item);
  if (!tagLabel && !taskId) return '—';
  return (
    <div className="d-flex flex-column gap-1">
      {tagLabel ? (
        <Badge bg="secondary" className="text-white align-self-start">
          {tagLabel}
        </Badge>
      ) : null}
      {taskId ? (
        <a
          href={`/tasks?task_id=${encodeURIComponent(taskId)}&open=1`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-break font-monospace"
          style={{ fontSize: '0.875rem' }}
        >
          {taskId}
        </a>
      ) : null}
    </div>
  );
}

const ErrorLogs = () => {
  const dispatch = useDispatch();
  const dateChart = useSelector(state => state.log.dateChart);
  const typeChart = useSelector(state => state.log.typeChart);
  const logs = useSelector(state => state.log.logs);
  const filters = useSelector(state => state.log.filters);

  const [search, setSearch] = useState(() => {
    const saved = getSavedFilter('search', '');
    return typeof saved === 'string' ? saved : '';
  });
  const debouncedSearch = useDebounce(search, 500);
  // Search applies only when at least 3 characters (reason or device_id contains)
  const effectiveSearch =
    search.trim().length < 3
      ? ''
      : (debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : '');
  const [page, setPage] = useState(() => {
    const saved = Number(getSavedFilter('page', 1));
    return Number.isFinite(saved) && saved >= 1 ? Math.floor(saved) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = Number(getSavedFilter('itemsPerPage', 10));
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 10;
  });
  const [device, setDevice] = useState(() => {
    const saved = getSavedFilter('device', '');
    return typeof saved === 'string' ? saved : '';
  });
  const [osVersion, setOsVersion] = useState(() => {
    const saved = getSavedFilter('osVersion', '');
    return typeof saved === 'string' ? saved : '';
  });
  const [appVersion, setAppVersion] = useState(() => {
    const saved = getSavedFilter('appVersion', '');
    return typeof saved === 'string' ? saved : '';
  });
  const [country, setCountry] = useState(() => {
    const saved = getSavedFilter('country', '');
    return typeof saved === 'string' ? saved : '';
  });
  const [errorCode, setErrorCode] = useState(() => {
    const saved = getSavedFilter('errorCode', '');
    return typeof saved === 'string' ? saved : '';
  });
  const [extraErrorCodes, setExtraErrorCodes] = useState([]);
  const skipSearchPageResetRef = useRef(true);

  const filterData = filters.data?.success ? filters.data.data : null;
  const deviceOptions = filterData?.devices || EMPTY_OPTIONS;
  const osVersionOptions = filterData?.os_versions || EMPTY_OPTIONS;
  const appVersionOptions = filterData?.app_versions || EMPTY_OPTIONS;
  const countryOptions = filterData?.countries || EMPTY_OPTIONS;
  const baseErrorCodes = filterData?.error_codes || EMPTY_OPTIONS;

  useEffect(() => {
    dispatch(fetchLogsDateChart());
    dispatch(fetchLogsTypeChart());
    dispatch(fetchLogsFilter());
  }, [dispatch]);

  // Persist filter state until Clear
  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}search`, JSON.stringify(search));
      localStorage.setItem(`${STORAGE_PREFIX}page`, JSON.stringify(page));
      localStorage.setItem(`${STORAGE_PREFIX}itemsPerPage`, JSON.stringify(itemsPerPage));
      localStorage.setItem(`${STORAGE_PREFIX}device`, JSON.stringify(device));
      localStorage.setItem(`${STORAGE_PREFIX}osVersion`, JSON.stringify(osVersion));
      localStorage.setItem(`${STORAGE_PREFIX}appVersion`, JSON.stringify(appVersion));
      localStorage.setItem(`${STORAGE_PREFIX}country`, JSON.stringify(country));
      localStorage.setItem(`${STORAGE_PREFIX}errorCode`, JSON.stringify(errorCode));
    } catch {
      // ignore storage errors
    }
  }, [search, page, itemsPerPage, device, osVersion, appVersion, country, errorCode]);

  // Reset page when effective search changes (skip first run so restored page is kept)
  useEffect(() => {
    if (skipSearchPageResetRef.current) {
      skipSearchPageResetRef.current = false;
      return;
    }
    setPage(1);
  }, [effectiveSearch]);

  useEffect(() => {
    dispatch(fetchLogs({
      page,
      items_per_page: itemsPerPage,
      q: effectiveSearch.trim() || undefined,
      device: device || undefined,
      os_version: osVersion || undefined,
      app_version: appVersion || undefined,
      country_code: country || undefined,
      reason: errorCode || undefined,
    }));
  }, [dispatch, page, itemsPerPage, effectiveSearch, device, osVersion, appVersion, country, errorCode]);

  useEffect(() => {
    const items = logs.data?.success ? logs.data.data.items : [];
    const found = new Set();
    items.forEach((item) => {
      const { tagLabel } = parseLogTag(item);
      const p = tagPrefix(tagLabel || item.tag || '');
      if (p) found.add(p);
    });
    const known = new Set(baseErrorCodes.map((o) => o.value).filter(Boolean));
    const extras = [...found].filter((p) => !known.has(p));
    setExtraErrorCodes(extras);
  }, [logs.data, baseErrorCodes]);

  const errorCodeOptions = [
    ...baseErrorCodes,
    ...extraErrorCodes.map((p) => ({ label: p, value: p })),
  ];

  const handleClear = () => {
    setSearch('');
    setDevice('');
    setOsVersion('');
    setAppVersion('');
    setCountry('');
    setErrorCode('');
    setItemsPerPage(10);
    setPage(1);
    clearSavedFilters();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setPage(1);
  };

  const onDeviceChange = (e) => {
    setPage(1);
    setDevice(e.target.value);
  };

  const onOsVersionChange = (e) => {
    setPage(1);
    setOsVersion(e.target.value);
  };

  const onAppVersionChange = (e) => {
    setPage(1);
    setAppVersion(e.target.value);
  };

  const onCountryChange = (e) => {
    setPage(1);
    setCountry(e.target.value);
  };

  const onErrorCodeChange = (e) => {
    setPage(1);
    setErrorCode(e.target.value);
  };

  const renderPaginationPages = () => {
    const totalPages = Math.ceil(logs.data.data.objects.total / itemsPerPage);
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push(2);
      pages.push('...');
      pages.push(totalPages - 1);
      pages.push(totalPages);
    }

    return pages;
  };

  const logItems = logs.data?.success ? logs.data.data.items : [];
  const totalEntries = logs.data?.success ? logs.data.data.objects.total : 0;

  return (
    <div className="dashboard-list-page">
      <h2 className="mb-4">Error Logs</h2>
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Errors by Date</Card.Title>
              {dateChart.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                  <Spinner animation="border" />
                </div>
              ) : dateChart.data && dateChart.data.success ? (
                <Line
                  data={{
                    labels: dateChart.data.data.map(item => item.date_string),
                    datasets: [
                      {
                        label: 'Errors',
                        data: dateChart.data.data.map(item => item.value),
                        fill: false,
                        borderColor: 'rgba(54, 162, 235, 1)',
                        backgroundColor: 'rgba(54, 162, 235, 0.2)',
                        tension: 0.3,
                        pointRadius: 3,
                        pointHoverRadius: 6,
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: false },
                      tooltip: { enabled: true }
                    },
                    scales: {
                      x: { title: { display: false } },
                      y: { beginAtZero: true, title: { display: false } }
                    }
                  }}
                  height={250}
                />
              ) : (
                <div style={{ height: 250 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Errors by Type</Card.Title>
              {typeChart.loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                  <Spinner animation="border" />
                </div>
              ) : typeChart.data && typeChart.data.success ? (
                <Bar
                  data={{
                    labels: typeChart.data.data.map(item => item.label),
                    datasets: [
                      {
                        label: 'Errors',
                        data: typeChart.data.data.map(item => item.value),
                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: false },
                      tooltip: { enabled: true }
                    },
                    scales: {
                      x: { title: { display: false } },
                      y: { beginAtZero: true, title: { display: false } }
                    }
                  }}
                  height={250}
                />
              ) : (
                <div style={{ height: 250 }} className="d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card>
        <Card.Body>
          <Card.Title>Error Details</Card.Title>
          <div className="mb-3">
            <Form.Control
              type="text"
              className="mb-2"
              placeholder="Search by reason, code, or device_id (min 3 characters)..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search.trim().length > 0 && search.trim().length < 3 ? (
              <Form.Text className="text-muted d-block mb-2">
                Enter at least 3 characters to search
              </Form.Text>
            ) : null}
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Form.Select
                value={device}
                onChange={onDeviceChange}
                style={{ minWidth: 140, flex: '1 1 140px' }}
              >
                {(deviceOptions.length ? deviceOptions : [{ label: 'All devices', value: '' }]).map((opt) => (
                  <option key={`device-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={osVersion}
                onChange={onOsVersionChange}
                style={{ minWidth: 140, flex: '1 1 140px' }}
              >
                {(osVersionOptions.length ? osVersionOptions : [{ label: 'All OS versions', value: '' }]).map((opt) => (
                  <option key={`os-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={appVersion}
                onChange={onAppVersionChange}
                style={{ minWidth: 140, flex: '1 1 140px' }}
              >
                {(appVersionOptions.length ? appVersionOptions : [{ label: 'All app versions', value: '' }]).map((opt) => (
                  <option key={`app-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={country}
                onChange={onCountryChange}
                style={{ minWidth: 140, flex: '1 1 140px' }}
              >
                {(countryOptions.length ? countryOptions : [{ label: 'All countries', value: '' }]).map((opt) => (
                  <option key={`country-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={errorCode}
                onChange={onErrorCodeChange}
                style={{ minWidth: 140, flex: '1 1 140px' }}
              >
                {(errorCodeOptions.length ? errorCodeOptions : [{ label: 'All error codes', value: '' }]).map((opt) => (
                  <option key={`error-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-secondary" type="button" onClick={handleClear}>
                Clear
              </Button>
              <Form.Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{ minWidth: 180, width: 'auto', flex: '0 0 auto' }}
                aria-label="Items per page"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </Form.Select>
            </div>
          </div>
          {logs.loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner animation="border" />
            </div>
          ) : logs.data && logs.data.success ? (
            <div>
              <ListDesktop>
                <Table hover bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th style={{ minWidth: 280 }}>TAG</th>
                      <th style={{ minWidth: 280, maxWidth: 360 }}>Reason</th>
                      <th style={{ minWidth: 220 }}>Device ID</th>
                      <th>Device Name</th>
                      <th>OS Version</th>
                      <th>App Version</th>
                      <th>Country Code</th>
                      <th>Created Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td style={{ minWidth: 280, whiteSpace: 'nowrap' }}><TagCell item={item} /></td>
                        <td style={{ minWidth: 280, maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {formatReason(item.reason)}
                        </td>
                        <td className="font-monospace" style={{ minWidth: 220, wordBreak: 'break-all', fontSize: '0.875rem' }}>
                          {item.device_id || '—'}
                        </td>
                        <td>{item.device_info?.model}</td>
                        <td>{item.device_info?.version}</td>
                        <td>{item.app_version}</td>
                        <td>{item.country_code}</td>
                        <td>{item.created_at ? new Date(item.created_at).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ListDesktop>

              <ListMobile>
                {logItems.map(item => (
                  <MobileCard key={item.id}>
                    <MobileCardTop>
                      <MobileCardTitle>#{item.id}</MobileCardTitle>
                    </MobileCardTop>
                    <MobileCardMeta>
                      {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                    </MobileCardMeta>
                    <MobileCardRow label="Tag">
                      <TagCell item={item} />
                    </MobileCardRow>
                    <MobileCardRow label="Reason">
                      {formatReason(item.reason)}
                    </MobileCardRow>
                    <MobileCardRow label="Device ID" mono>
                      {item.device_id || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="Device">
                      {item.device_info?.model || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="OS Version">
                      {item.device_info?.version || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="App Version">
                      {item.app_version || '—'}
                    </MobileCardRow>
                    <MobileCardRow label="Country">
                      {item.country_code || '—'}
                    </MobileCardRow>
                  </MobileCard>
                ))}
              </ListMobile>

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 mt-3">
                <div>
                  Showing {logItems.length} of {totalEntries} entries
                </div>
                <nav>
                  <ul className="pagination mb-0 flex-wrap">
                    <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
                    </li>
                    {renderPaginationPages().map((pageNum, index) => (
                      <li
                        key={index}
                        className={`page-item${pageNum === page ? ' active' : ''}${pageNum === '...' ? ' disabled' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => pageNum !== '...' && handlePageChange(pageNum)}
                          disabled={pageNum === '...'}
                        >
                          {pageNum}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item${page === Math.ceil(totalEntries / itemsPerPage) ? ' disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page + 1)} disabled={page === Math.ceil(totalEntries / itemsPerPage)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          ) : (
            <div style={{ height: 200 }} className="d-flex align-items-center justify-content-center text-muted">
              No data
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ErrorLogs;
